import { useEffect, useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { Badge, Box, Divider, Typography, Stack, MenuItem, IconButton, Popover, Tooltip } from '@mui/material';
import io from 'socket.io-client';

import { authContext } from '../../../routes';
import axiosInstance from '../../../api';
import Iconify from '../../../components/iconify';
import SERVER_API_URL from '../../../config';

export default function NotificationsPopover() {
  const { user } = useContext(authContext);
  const [totalUnRead, setTotalUnRead] = useState(0);
  const navigate = useNavigate();
  const [socket, setSocket] = useState(null);
  const [open, setOpen] = useState(null);
  const currentLocation = useLocation();

  const canReadPublicChat = user?.role?.permissions?.publicChat?.read;
  const canReadDistrictChat = user?.role?.permissions?.allDistrictChat?.read;
  const canReadChurchChat = user?.role?.permissions?.allChurchChat?.read;
  const canReadRoleChat = user?.role?.permissions?.allRoleChat?.read;
  const canReadCategoryChat = user?.role?.permissions?.allCategoryChat?.read;
  const canReadPersonalChat = user?.role?.permissions?.personalChat?.read;

  const canReadOwnDistrictChat = user?.role?.permissions?.ownDistrictChat?.read;
  const canReadOwnChurchChat = user?.role?.permissions?.ownChurchChat?.read;
  const canReadOwnRoleChat = user?.role?.permissions?.ownRoleChat?.read;
  const canReadOwnCategoryChat = user?.role?.permissions?.ownCategoryChat?.read;

  useEffect(() => {
    const getAllNotifications = async () => {
      try {
        const response = await axiosInstance.post(`${SERVER_API_URL}/chat/getUnreadChats/${user.id}`);

        if (response.status === 200) {
          const unReadChatMessage = response.data;
          setTotalUnRead(() => {
            let newTotal = 0;
            if (canReadPublicChat) {
              const filteredPublicChat = unReadChatMessage.publicChats.filter((chat) => chat.sender.id !== user.id);
              newTotal += filteredPublicChat.length;
            }

            if (canReadDistrictChat) {
              const filteredDistrictChat = unReadChatMessage.districtChats.filter((chat) => chat.sender.id !== user.id);
              newTotal += filteredDistrictChat.length;
            }

            if (canReadChurchChat) {
              const filteredChurchChat = unReadChatMessage.churchChats.filter((chat) => chat.sender.id !== user.id);
              newTotal += filteredChurchChat.length;
            }

            if (canReadRoleChat) {
              const filteredRoleChat = unReadChatMessage.roleChats.filter((chat) => chat.sender.id !== user.id);
              newTotal += filteredRoleChat.length;
            }

            if (canReadCategoryChat) {
              const filteredCategoryChat = unReadChatMessage.categoryChats.filter((chat) => chat.sender.id !== user.id);
              newTotal += filteredCategoryChat.length;
            }

            if (canReadPersonalChat) {
              const filteredPersonalChat = unReadChatMessage.personalChats.filter(
                (chat) => chat.sender.id !== user.id && chat.target.userId === user.id
              );
              newTotal += filteredPersonalChat.length;
            }

            if (canReadOwnDistrictChat) {
              const filteredOwnDistrictChat = unReadChatMessage.districtChats.filter(
                (chat) => chat.target.districtId === user.church.district.id
              );
              newTotal += filteredOwnDistrictChat.length;
            }

            if (canReadOwnChurchChat) {
              const filteredOwnChurchChat = unReadChatMessage.churchChats.filter(
                (chat) => chat.target.churchId === user.church.id
              );
              newTotal += filteredOwnChurchChat.length;
            }

            if (canReadOwnRoleChat) {
              const filteredOwnRoleChat = unReadChatMessage.roleChats.filter(
                (chat) => chat.target.roleId === user.role.id
              );
              newTotal += filteredOwnRoleChat.length;
            }

            if (canReadOwnCategoryChat) {
              const filteredOwnCategoryChat = unReadChatMessage.categoryChats.filter(
                (chat) => chat.target.categoryId === user.category.id
              );
              newTotal += filteredOwnCategoryChat.length;
            }

            return newTotal;
          });
        }
      } catch (error) {
        console.error('Error fetching unread chats:', error);
      }
    };
    getAllNotifications();
  }, [
    user,
    canReadPublicChat,
    canReadDistrictChat,
    canReadChurchChat,
    canReadRoleChat,
    canReadCategoryChat,
    canReadPersonalChat,
    canReadOwnDistrictChat,
    canReadOwnChurchChat,
    canReadOwnRoleChat,
    canReadOwnCategoryChat,
  ]);

  useEffect(() => {
    const newSocket = io(`${SERVER_API_URL}`, { transports: ['websocket'] });
    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (socket && currentLocation.pathname !== '/dashboard/chat') {
      socket.on('onMessage', (newMessage) => {
        if (
          (newMessage.target.public && canReadPublicChat && newMessage.sender.id !== user.id) ||
          (newMessage.target.districtId && canReadDistrictChat && newMessage.sender.id !== user.id) ||
          (newMessage.target.churchId && canReadChurchChat && newMessage.sender.id !== user.id) ||
          (newMessage.target.roleId && canReadRoleChat && newMessage.sender.id !== user.id) ||
          (newMessage.target.categoryId && canReadCategoryChat && newMessage.sender.id !== user.id) ||
          (canReadPersonalChat && newMessage.target.userId === user.id && newMessage.sender.id !== user.id) ||
          (canReadOwnDistrictChat &&
            newMessage.target.districtId === user.church.district.id &&
            newMessage.sender.id !== user.id) ||
          (canReadOwnChurchChat && newMessage.target.churchId === user.church.id && newMessage.sender.id !== user.id) ||
          (canReadOwnRoleChat && newMessage.target.roleId === user.role.id && newMessage.sender.id !== user.id) ||
          (canReadOwnCategoryChat &&
            newMessage.target.categoryId === user.category.id &&
            newMessage.sender.id !== user.id)
        ) {
          setTotalUnRead((totalUnRead) => totalUnRead + 1);
        }
      });
    }

    return () => {
      if (socket) {
        socket.off('onMessage');
      }
    };
  }, [
    socket,
    user,
    canReadPublicChat,
    canReadDistrictChat,
    canReadChurchChat,
    canReadRoleChat,
    canReadCategoryChat,
    canReadPersonalChat,
    canReadOwnDistrictChat,
    canReadOwnChurchChat,
    canReadOwnRoleChat,
    canReadOwnCategoryChat,
    currentLocation,
  ]);

  const handleNavigate = () => {
    setTotalUnRead(0);
    navigate('/dashboard/chat');
    setOpen(null);
  };

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };
  return (
    <>
      <Tooltip title="ማስታወቂያ" arrow>
        <IconButton
          onClick={handleOpen}
          sx={{
            p: 0,
            ...(open && {
              '&:before': {
                zIndex: 1,
                content: "''",
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                position: 'absolute',
                bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
              },
            }),
          }}
        >
          <Badge badgeContent={currentLocation.pathname !== '/dashboard/chat' ? totalUnRead : 0} color="error">
            <Iconify icon="eva:bell-fill" />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 0,
            mt: 1.5,
            ml: 0.75,
            width: 180,
            '& .MuiMenuItem-root': {
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            ያልተነበቡ {totalUnRead} መልዕክቶች አሉዎት
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          <MenuItem key="view" onClick={handleNavigate}>
            <Iconify icon={'eva:profile-2-fill'} /> ሁሉንም ይመልከቱ
          </MenuItem>
        </Stack>
      </Popover>
    </>
  );
}
